import ContentImageMediaSlider from "../../content-image-media-slider";
import { getMediaRecordPath, getMediaUrl } from "@/utils/media";
import { useEffect, useRef } from "react";

const AUTOPLAY_READY_EVENTS = ["loadeddata", "canplay"];

const DESKTOP_MEDIA_MODAL_STAGE_CLASS =
  "relative flex h-full w-auto aspect-[3/4] max-w-full items-center justify-center overflow-hidden bg-black";

const MOBILE_DETAIL_MEDIA_STAGE_CLASS =
  "relative flex h-full w-full items-center justify-center overflow-hidden bg-black";

const MediaModalStage = ({ children, variant = "desktop" }) => (
  <div
    className={
      variant === "mobile-detail"
        ? MOBILE_DETAIL_MEDIA_STAGE_CLASS
        : DESKTOP_MEDIA_MODAL_STAGE_CLASS
    }
    data-media-modal-stage
    data-media-stage-variant={variant}
  >
    {children}
  </div>
);

const getVideoPosterPath = (post) => {
  const media = post?.media?.[0];
  const cover = post?.postMediaCover?.[0];

  return (
    cover?.thumbnailPath ||
    cover?.mediumPath ||
    cover?.coverPath ||
    media?.thumbnailPath ||
    media?.placeholderPath ||
    media?.placeholder ||
    media?.mediumPath ||
    ""
  );
};

const useAutoplayingModalVideo = ({ isEnabled, resetKey }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!isEnabled || !video) return undefined;

    const playVideo = () => {
      video.play?.()?.catch?.(() => {});
    };

    video.muted = true;
    video.playsInline = true;
    video.load();

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      playVideo();
    } else {
      AUTOPLAY_READY_EVENTS.forEach((eventName) => {
        video.addEventListener(eventName, playVideo, { once: true });
      });
    }

    return () => {
      AUTOPLAY_READY_EVENTS.forEach((eventName) => {
        video.removeEventListener(eventName, playVideo);
      });
      video.pause?.();
    };
  }, [isEnabled, resetKey]);

  return videoRef;
};

const ContentVideoMedia = ({ post, variant }) => {
  const media = post?.media?.[0];
  const videoUrl = getMediaUrl(getMediaRecordPath(media));
  const posterUrl = getMediaUrl(getVideoPosterPath(post));
  const videoRef = useAutoplayingModalVideo({
    isEnabled: !!videoUrl,
    resetKey: post?.id || videoUrl,
  });

  return (
    <MediaModalStage variant={variant}>
      <video
        ref={videoRef}
        src={videoUrl}
        poster={posterUrl || undefined}
        className="block h-full w-full object-contain"
        autoPlay
        muted
        controls
        // 조작줄 자체는 남긴다(멈춤·되감기가 필요하다). 다만 ⋮ 안의 「동영상 저장」과
        // 외부 재생은 막는다 — 우리 영상이 그대로 내려받아지는 통로였다(2026-08-11 검수).
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        playsInline
        preload="auto"
      />
    </MediaModalStage>
  );
};

const ContentImageMedia = ({ slides, variant }) => (
  <MediaModalStage variant={variant}>
    <ContentImageMediaSlider slides={slides} />
  </MediaModalStage>
);

const ContentMediaSection = ({
  selectedPostInModal,
  className,
  variant = "desktop",
}) => {
  const mediaType = selectedPostInModal?.media?.[0]?.mediaType || "";
  const shouldRenderImages =
    mediaType.includes("image") || mediaType.includes("livephoto");
  const shouldRenderVideo = mediaType.includes("video");

  if (!shouldRenderImages && !shouldRenderVideo) return null;

  if (shouldRenderVideo) {
    return (
      <div className={className}>
        <ContentVideoMedia post={selectedPostInModal} variant={variant} />
      </div>
    );
  }

  return (
    <div className={className}>
      <ContentImageMedia slides={selectedPostInModal?.media} variant={variant} />
    </div>
  );
};

export default ContentMediaSection;
